/* eslint-disable react-hooks/exhaustive-deps */
import './App.scss';

import { FunctionComponent, Suspense, useEffect, useState } from 'react';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { LinkAdapterProvider } from '@gaiads/telia-react-component-library';
import { Address, Person, UserOrganizationResponse, UserRole } from '@cdaas/api-clients/dist/asset-user-openapi';

import Approval from './Pages/Approval/Approval';
import Orders from './Pages/Orders/Orders';
import Shop from './Pages/Shop/Shop';
import Onboarding from './Pages/Onboarding/Onboarding';
import MyAssets from './Pages/MyAssets/MyAssets';
import UsersAdmin from './Pages/UsersAdmin/UsersAdmin';
import Organization from './Pages/Organization/Organization';
import OrganizationAssets from './Pages/OrganizationAssets/OrganizationAssets';
import PhoneNumberVerificationFlowContainer from './Components/PhoneVerification/PhoneNumberVerificationFlowContainer';
import ImportUsers from './Pages/UsersAdmin/ImportUsers/ImportUsers';
import { LocalBundleEnvelopeWithChosenMainVariant } from './types';
import * as actions from './store/actions';
import { RootState } from './store/reducers';
import GlobalGrowlNotification from './Components/Notification/GlobalGrowlNotification';
import GlobalModal from './Components/GlobalModal/GlobalModal';
import TopErrorBoundary from './Components/TopErrorBoundary/TopErrorBoundary';
import {
  checkIsSelectedOrganizationOwn,
  getAUser,
  getStorageItem,
  getStorageItems,
  hasUserARequiredRole,
  isWebshopEnabled,
  openExternalUrlFromQueryParams,
  removeStorageItems,
} from './utils';
import {
  shopSetDeliveryDetails,
  shopSetProduct,
  shopSetOrderOnBehalfUser,
  getPendingApprovals,
  authSetSelectedOrganization,
  setFeatureFlags,
  shopSetOrderForOrganization,
} from './store/actions';
import { RouterLinkAdapter } from './utils/linkAdapters';
import Authentication from './Pages/Authentication/Authentication';
import NavigationLayout from './Layouts/Navigation/NavigationLayout';
import BankIdVerificationContainer from './Components/BankIdVerification/BankIdVerificationContainer';
import OrganizationOrders from './Pages/OrganizationOrders/OrganizationOrders';
import MinBedriftHelp from './Pages/MinBedrift/MinBedriftHelp';
import WebshopLoader from './Components/WebshopLoader/WebshopLoader';
import Home from './Pages/Home/Home';
import CommonErrorContent from './Pages/Authentication/CommonErrorContent/CommonErrorContent';
import { useTranslation } from 'react-i18next';

const App: FunctionComponent = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation('shop');

  const [featureFlagsFetched, setFeatureFlagsFetched] = useState(false);

  const tryAutoAuth = () => dispatch(actions.tryAutoAuth());
  const devAuthentication = () => dispatch(actions.authenticate('dummy-code', 'www.dummy-address.com', history.location.pathname));
  const logout = () => dispatch(actions.executeLogout());
  const storeRequestedPath = (path: string | null) => dispatch(actions.authSetRequestedPath(path));
  const autoAuthFailed = useSelector((state: RootState) => state.authentication.autoAuthFailed);
  const authenticated = useSelector((state: RootState) => state.authentication.authenticated);
  const authSeqReady = useSelector((state: RootState) => state.authentication.authSequenceReady);
  const accessTokenExpiryDate = useSelector((state: RootState) => state.authentication.credentials.expiryDate);
  const phoneNumberVerification = useSelector((state: RootState) => state.authentication.phoneNumberVerification);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);
  const bankIdVerificationNeeded = useSelector((state: RootState) => state.authentication.bankIdVerificationNeeded);
  const maintenanceModeEnabled = useSelector((state: RootState) => state.featureFlags.FEATURE_FLAG_MAINTENANCE_MODE_ENABLED);
  const shopMaintenanceModeEnabled = useSelector((state: RootState) => state.featureFlags.FEATURE_FLAG_MAINTENANCE_MODE_SHOP_ENABLED);

  const isSelectedOrganizationOwn = checkIsSelectedOrganizationOwn(ownOrganization?.id, selectedOrganization?.id);

  // You can fetch in this function all needed session or local storage stuff.
  // If you need then on app init and can be saved somewhere like e.g. redux.
  const saveExistingStorageItemsToStore = (): void => {
    const [product, deliveryDetails, selectedOrganization] = getStorageItems(['tItem', 'tDelivery', 'tSelectedOrganization'], 'session', [
      true,
      true,
      true,
    ]);
    if (product) dispatch(shopSetProduct(product as LocalBundleEnvelopeWithChosenMainVariant));
    if (deliveryDetails) {
      const { address, contact, isHomeDelivery } = deliveryDetails as { address: Address; contact: Person; isHomeDelivery: boolean };
      dispatch(shopSetDeliveryDetails(address, contact, isHomeDelivery));
    }
    if (selectedOrganization) {
      dispatch(authSetSelectedOrganization(selectedOrganization as UserOrganizationResponse));
    }
  };

  useEffect(() => {
    dispatch(setFeatureFlags()).finally(() => setFeatureFlagsFetched(true));
  }, []);

  useEffect(() => {
    if (featureFlagsFetched) {
      if (location.pathname.includes('authentication')) {
        storeRequestedPath(null);
      } else {
        storeRequestedPath(location.pathname);
      }
    }
  }, [featureFlagsFetched]);

  useEffect(() => {
    if (!location.hash) {
      globalThis.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  useEffect(() => {
    openExternalUrlFromQueryParams(location.search, history);
  }, [location.search, history]);

  useEffect(() => {
    const saveOrderOnBehalfDataFromStorage = async (): Promise<void> => {
      const OOB = getStorageItem<{ userId?: string; userGroupId?: string }>('tOOB', 'session', true);
      if (OOB?.userId && OOB.userGroupId) {
        const user = await getAUser(OOB.userId, OOB.userGroupId);
        if (user) {
          dispatch(shopSetOrderOnBehalfUser(user));
        }
      }
    };
    if (featureFlagsFetched && authenticated) {
      saveOrderOnBehalfDataFromStorage();
      dispatch(getPendingApprovals());
    }
  }, [authenticated, dispatch, featureFlagsFetched]);

  useEffect(() => {
    const setOrderForOrganizationFromStorage = async (): Promise<void> => {
      const [orderingForOrganization] = getStorageItems(['tOrderForOrganization'], 'session', [false]);
      if (orderingForOrganization) {
        dispatch(shopSetOrderForOrganization());
      }
    };
    if (featureFlagsFetched && authenticated) {
      setOrderForOrganizationFromStorage();
    }
  }, [authenticated, dispatch, featureFlagsFetched]);

  useEffect(() => {
    if (featureFlagsFetched) {
      saveExistingStorageItemsToStore();
      // Check if CIAM authentication process is already ongoing
      // tState is in localStorage during CIAM authentication
      const state = getStorageItem<string>('tState', 'local', true);
      if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_AUTH_SKIP === 'true') {
        devAuthentication();
      } else if (!state && location.pathname !== '/authentication/logout') {
        tryAutoAuth();
      } else if (
        !!state &&
        location.pathname !== '/authentication/authenticated' &&
        location.pathname !== '/authentication/logout' &&
        location.pathname !== '/authentication/login-failed'
      ) {
        history.push('/authentication/login' + history.location.search); // retain query parameters
      }
      return () => {
        removeStorageItems(
          ['tItem', 'tDelivery', 'tSelectedDelivery', 'tOrderResp', 'tOOB', 'tOrderForOrganization', 'tSelectedOrganization'],
          'session',
        );
      };
    }
  }, [featureFlagsFetched]);

  useEffect(() => {
    if (featureFlagsFetched && autoAuthFailed) {
      console.error('Auto authentication failed!');
      // @TODO redirect to login here or maybe directly to CIAM
      history.push('/authentication/login');
    }
  }, [autoAuthFailed, featureFlagsFetched]);

  useEffect(() => {
    if (maintenanceModeEnabled && !location.pathname.includes('/authentication/maintenance-mode')) {
      history.push('/authentication/maintenance-mode');
    }
  }, [maintenanceModeEnabled, location.pathname]);

  const handleAppClick = (): void => {
    const sec2 = 2 * 1000;
    // If refresh token is expired or is just about to expire (handle it as expired) then do full logout
    if (authenticated && accessTokenExpiryDate?.getTime()) {
      if (Date.parse(accessTokenExpiryDate.toString()) - Date.now() < sec2) {
        logout();
      }
    }
  };

  if (!featureFlagsFetched) {
    return <></>;
  }

  return (
    <div data-testid="app" onClick={handleAppClick}>
      {/* PLACEHOLDER for temporary notification - see commit adb9235d46c6ef32afad3f0538265c136f113a98.
      The notification is visible in the top of the page for the users who are not authenticated */}
      <Suspense fallback="loading">
        {/* @ts-expect-error Type '{ children: Element[]; linkAdapter: LinkAdapter; }' is not assignable to type 'IntrinsicAttributes & LinkAdapterProviderProps'. */}
        <LinkAdapterProvider linkAdapter={RouterLinkAdapter}>
          <GlobalGrowlNotification />
          <TopErrorBoundary>
            <GlobalModal />
            {phoneNumberVerification.needsVerification && !!phoneNumberVerification.phoneNumber && (
              <PhoneNumberVerificationFlowContainer phoneNumber={phoneNumberVerification.phoneNumber} />
            )}
            {bankIdVerificationNeeded && <BankIdVerificationContainer />}
            <Switch>
              <Route path="/authentication" component={Authentication} />
              {authenticated && (
                <Route
                  path="/home"
                  render={() => (
                    <NavigationLayout>
                      <Home />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && (
                <Route
                  path="/shop"
                  render={() => (
                    <NavigationLayout>
                      <Shop />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && isWebshopEnabled(selectedOrganization, ownOrganization) && (
                <Route
                  path="/webshop"
                  render={() => (
                    <NavigationLayout>
                      {shopMaintenanceModeEnabled ? (
                        <CommonErrorContent
                          title={t('shopMaintenanceMode.title')}
                          subtitle={t('shopMaintenanceMode.subtitle')}
                          content={t('shopMaintenanceMode.content')}
                        />
                      ) : (
                        <WebshopLoader />
                      )}
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Approver]) && (
                <Route
                  path="/approval/:id?"
                  render={() => (
                    <NavigationLayout>
                      <Approval />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.User, UserRole.UserLite]) && (
                <Route
                  path="/orders/:id?"
                  render={() => (
                    <NavigationLayout>
                      <Orders />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.User, UserRole.UserLite]) && isSelectedOrganizationOwn && (
                <Route
                  path="/myassets/:id?"
                  render={() => (
                    <NavigationLayout>
                      <MyAssets />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/organizationassets/:id?"
                  render={() => (
                    <NavigationLayout>
                      <OrganizationAssets />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/organizationorders"
                  render={() => (
                    <NavigationLayout>
                      <OrganizationOrders />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/onboarding"
                  render={() => (
                    <NavigationLayout>
                      <Onboarding />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/users/import"
                  render={() => (
                    <NavigationLayout>
                      <ImportUsers />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/users"
                  render={() => (
                    <NavigationLayout>
                      <UsersAdmin />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/organization"
                  render={() => (
                    <NavigationLayout>
                      <Organization />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && hasUserARequiredRole([UserRole.Admin]) && (
                <Route
                  path="/minbedrift/help"
                  render={() => (
                    <NavigationLayout>
                      <MinBedriftHelp />
                    </NavigationLayout>
                  )}
                />
              )}
              {authenticated && authSeqReady && <Redirect to="/home" />}
              {!authenticated && authSeqReady && <Redirect to="/authentication/login" />}
            </Switch>
          </TopErrorBoundary>
        </LinkAdapterProvider>
      </Suspense>
    </div>
  );
};

export default App;
